import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import PageLoading from './components/common/PageLoading';
import { setMessageApi } from './api/client';

// Lazy-loaded Pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ConsolidatedDashboard = lazy(() => import('./pages/dashboard/ConsolidatedDashboard'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const InviteAccept = lazy(() => import('./pages/auth/InviteAccept'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));
const TwoFactorSetup = lazy(() => import('./pages/auth/TwoFactorSetup'));
const SecuritySettings = lazy(() => import('./pages/auth/SecuritySettings'));

// Inventory Pages (lazy)
const ItemList = lazy(() => import('./pages/inventory/ItemList'));
const StockDashboard = lazy(() => import('./pages/inventory/StockDashboard'));
const SerialNumberDashboard = lazy(() => import('./pages/inventory/SerialNumberDashboard'));
const LotBatchDashboard = lazy(() => import('./components/inventory/LotBatchDashboard'));
const CycleCountDashboard = lazy(() => import('./pages/inventory/CycleCountDashboard'));
const StockMovements = lazy(() => import('./pages/inventory/StockMovements'));
const Locations = lazy(() => import('./pages/inventory/Locations'));
const TransferOrderList = lazy(() => import('./pages/inventory/TransferOrderList'));

// Purchase Order Pages (lazy)
const PurchaseOrderList = lazy(() => import('./pages/purchase-orders/PurchaseOrderList'));
const PurchaseOrderForm = lazy(() => import('./pages/purchase-orders/PurchaseOrderForm'));
const PurchaseOrderApproval = lazy(() => import('./pages/purchase-orders/PurchaseOrderApproval'));
const ReceivingInterface = lazy(() => import('./pages/purchase-orders/ReceivingInterface'));

// Sales Order Pages (lazy)
const SalesOrderList = lazy(() => import('./pages/sales-orders/SalesOrderList'));
const SalesOrderForm = lazy(() => import('./pages/sales-orders/SalesOrderForm'));
const FulfillmentTracking = lazy(() => import('./pages/sales-orders/FulfillmentTracking'));
const ShippingInterface = lazy(() => import('./pages/sales-orders/ShippingInterface'));

// Invoice Pages (lazy)
const InvoiceList = lazy(() => import('./pages/invoices/InvoiceList'));
const InvoiceForm = lazy(() => import('./pages/invoices/InvoiceForm'));
const InvoicePreview = lazy(() => import('./pages/invoices/InvoicePreview'));

// Payment Pages (lazy)
const PaymentList = lazy(() => import('./pages/payments/PaymentList'));
const PaymentAllocation = lazy(() => import('./pages/payments/PaymentAllocation'));
const UnallocatedPayments = lazy(() => import('./pages/payments/UnallocatedPayments'));

// BOM Pages (lazy)
const BOMList = lazy(() => import('./pages/bom/BOMList'));
const BOMTreeView = lazy(() => import('./pages/bom/BOMTreeView'));
const BOMCostAnalysis = lazy(() => import('./pages/bom/BOMCostAnalysis'));

// Assembly Pages (lazy)
const AssemblyOrderList = lazy(() => import('./pages/assembly/AssemblyOrderList'));
const AssemblyWizard = lazy(() => import('./pages/assembly/AssemblyWizard'));

// Demand Planning Pages (lazy)
const DemandPlanningDashboard = lazy(() => import('./pages/demand-planning/DemandPlanningDashboard'));
const ForecastWizard = lazy(() => import('./pages/demand-planning/ForecastWizard'));
const ForecastVisualization = lazy(() => import('./pages/demand-planning/ForecastVisualization'));
const ForecastComparison = lazy(() => import('./pages/demand-planning/ForecastComparison'));
const ReorderPointConfig = lazy(() => import('./pages/demand-planning/ReorderPointConfig'));
const SafetyStockCalculator = lazy(() => import('./pages/demand-planning/SafetyStockCalculator'));
const PurchaseRequisitionList = lazy(() => import('./pages/demand-planning/PurchaseRequisitionList'));
const PurchaseRequisitionForm = lazy(() => import('./pages/demand-planning/PurchaseRequisitionForm'));
const RequisitionApproval = lazy(() => import('./pages/demand-planning/RequisitionApproval'));

// Report Pages (lazy)
const ReportSelector = lazy(() => import('./pages/reports/ReportSelector'));
const CashFlowReport = lazy(() => import('./pages/reports/CashFlowReport'));
const AgingReport = lazy(() => import('./pages/reports/AgingReport'));
const CustomerStatement = lazy(() => import('./pages/reports/CustomerStatement'));
const VendorStatement = lazy(() => import('./pages/reports/VendorStatement'));
const InventoryReports = lazy(() => import('./pages/reports/InventoryReports'));
const CustomReportBuilder = lazy(() => import('./pages/reports/CustomReportBuilder'));
const ScheduledReports = lazy(() => import('./pages/reports/ScheduledReports'));
const ReportHistory = lazy(() => import('./pages/reports/ReportHistory'));

// Settings Pages (lazy)
const ThemeSettings = lazy(() => import('./pages/settings/ThemeSettings'));
const GeneralSettings = lazy(() => import('./pages/settings/GeneralSettings'));
const OAuthProviders = lazy(() => import('./pages/settings/OAuthProviders'));
const LinkedAccounts = lazy(() => import('./pages/settings/LinkedAccounts'));
const AccountingSettings = lazy(() => import('./pages/settings/AccountingSettings'));
const RegionalSettings = lazy(() => import('./pages/settings/RegionalSettings'));
const TaxRules = lazy(() => import('./pages/settings/TaxRules'));
const Integrations = lazy(() => import('./pages/settings/Integrations'));
const CompanyTemplates = lazy(() => import('./pages/settings/CompanyTemplates'));

// Accounting Pages (lazy)
const ChartOfAccounts = lazy(() => import('./pages/accounting/ChartOfAccounts'));
const JournalEntries = lazy(() => import('./pages/accounting/JournalEntries'));
const AccountingReports = lazy(() => import('./pages/accounting/AccountingReports'));
const TrialBalance = lazy(() => import('./pages/reports/TrialBalance'));
const IncomeStatement = lazy(() => import('./pages/reports/IncomeStatement'));
const BalanceSheet = lazy(() => import('./pages/reports/BalanceSheet'));
const AccountActivity = lazy(() => import('./pages/reports/AccountActivity'));

// User Management Pages (lazy)
const UserList = lazy(() => import('./pages/users/UserList'));
const RoleManagement = lazy(() => import('./pages/users/RoleManagement'));

// Bills/Expenses Pages (lazy)
const ExpenseList = lazy(() => import('./pages/expenses/ExpenseList'));

// Customer Pages (lazy)
const CustomerList = lazy(() => import('./pages/customers/CustomerList'));

// Vendor Pages (lazy)
const VendorList = lazy(() => import('./pages/vendors/VendorList'));

// Banking Pages (lazy)
const BankAccountList = lazy(() => import('./pages/banking/BankAccountList'));
const BankReconciliationIndex = lazy(() => import('./pages/banking/BankReconciliationIndex'));
const BankReconciliation = lazy(() => import('./pages/banking/BankReconciliation'));
const BankTransfers = lazy(() => import('./pages/banking/BankTransfers'));
const BankStatements = lazy(() => import('./pages/banking/BankStatements'));

// Transaction Pages (lazy)
const CashLedger = lazy(() => import('./pages/transactions/CashLedger'));
const ReconciliationRules = lazy(() => import('./pages/transactions/ReconciliationRules'));

// Quote Pages (lazy)
const QuoteList = lazy(() => import('./pages/quotes/QuoteList'));
const QuotePreview = lazy(() => import('./pages/quotes/QuotePreview'));

// RMA Pages (lazy)
const RMAList = lazy(() => import('./pages/rmas/RMAList'));

// Budget Pages (lazy)
const BudgetList = lazy(() => import('./pages/budgets/BudgetList'));
const BudgetAnalysis = lazy(() => import('./pages/budgets/BudgetAnalysis'));

// Fixed Assets Pages (lazy)
const AssetList = lazy(() => import('./pages/assets/AssetList'));
const DepreciationSchedule = lazy(() => import('./pages/assets/DepreciationSchedule'));

// Receipt Scanning Pages (lazy)
const ReceiptList = lazy(() => import('./pages/receipts/ReceiptList'));
const ReceiptScanner = lazy(() => import('./pages/receipts/ReceiptScanner'));

// Document Management Pages (lazy)
const DocumentList = lazy(() => import('./pages/documents/DocumentList'));
const DocumentViewer = lazy(() => import('./pages/documents/DocumentViewer'));
const TemplateManager = lazy(() => import('./pages/documents/TemplateManager'));

// Project Costing Pages (lazy)
const ProjectList = lazy(() => import('./pages/projects/ProjectList'));
const ProjectDashboard = lazy(() => import('./pages/projects/ProjectDashboard'));
const TimeEntryList = lazy(() => import('./pages/projects/TimeEntryList'));
const ProjectProfitability = lazy(() => import('./pages/projects/ProjectProfitability'));

// Organization Pages (lazy)
const OrganizationSelect = lazy(() => import('./pages/organizations/OrganizationSelect'));
const OrganizationList = lazy(() => import('./pages/organizations/OrganizationList'));
const OrganizationForm = lazy(() => import('./pages/organizations/OrganizationForm'));
const OrganizationSettings = lazy(() => import('./pages/organizations/OrganizationSettings'));
const OrganizationGroups = lazy(() => import('./pages/organizations/OrganizationGroups'));
const UserManagement = lazy(() => import('./pages/organizations/UserManagement'));
const ConsolidatedFinancials = lazy(() => import('./pages/organizations/ConsolidatedFinancials'));
const IntercompanyTransactions = lazy(() => import('./pages/organizations/IntercompanyTransactions'));
const OrganizationComparison = lazy(() => import('./pages/organizations/OrganizationComparison'));
const GroupCashFlow = lazy(() => import('./pages/organizations/GroupCashFlow'));

// API Management Pages (lazy) - Sprint 11
const APIManagementDashboard = lazy(() => import('./pages/api-management/APIManagementDashboard'));
const RateLimitingDashboard = lazy(() => import('./pages/api-management/RateLimitingDashboard'));
const WebhookManagementDashboard = lazy(() => import('./pages/api-management/WebhookManagementDashboard'));

// Payroll Pages (lazy)
const EmployeeList = lazy(() => import('./pages/payroll/EmployeeList'));
const PayrollRunList = lazy(() => import('./pages/payroll/PayrollRunList'));
const TimesheetEntry = lazy(() => import('./pages/payroll/TimesheetEntry'));
const TimesheetApproval = lazy(() => import('./pages/payroll/TimesheetApproval'));
const TaxSetup = lazy(() => import('./pages/payroll/TaxSetup'));
const PayStubView = lazy(() => import('./pages/payroll/PayStubView'));
const PayrollReports = lazy(() => import('./pages/payroll/PayrollReports'));

// Security/Administration Pages (lazy)
const AuditLogDashboard = lazy(() => import('./pages/audit/AuditLogDashboard'));

// Performance/Administration Pages (lazy)
const CacheManagement = lazy(() => import('./pages/performance/CacheManagement'));
const ArchivalManagement = lazy(() => import('./pages/performance/ArchivalManagement'));
const UsageAnalytics = lazy(() => import('./pages/performance/UsageAnalytics'));
const MultiRegionDashboard = lazy(() => import('./pages/multi-region/MultiRegionDashboard'));
const DataRetentionSettings = lazy(() => import('./pages/security/DataRetentionSettings'));

// Navigation Hub (lazy)
const NavigationHub = lazy(() => import('./pages/navigation/NavigationHub'));

function App() {
  // Get context-aware message API from Ant Design App component
  const { message } = AntApp.useApp();

  // Set message API for axios interceptors
  useEffect(() => {
    setMessageApi(message);
  }, [message]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/accept" element={<InviteAccept />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />

          {/* Organization Selection (Protected but outside MainLayout) */}
          <Route
            path="/organizations/select"
            element={
              <ProtectedRoute>
                <OrganizationSelect />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard/consolidated" element={<ConsolidatedDashboard />} />
          <Route path="2fa-setup" element={<TwoFactorSetup />} />

          {/* Settings Routes */}
          <Route path="settings">
            <Route path="security" element={<SecuritySettings />} />
            <Route path="theme" element={<ThemeSettings />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="oauth-providers" element={<OAuthProviders />} />
            <Route path="linked-accounts" element={<LinkedAccounts />} />
            <Route path="fiscal-periods" element={<AccountingSettings />} />
            <Route path="tax-rules" element={<TaxRules />} />
            <Route path="regional" element={<RegionalSettings />} />
            <Route path="templates" element={<CompanyTemplates />} />
            <Route path="integrations" element={<Integrations />} />
          </Route>

          {/* Accounting Routes */}
          <Route path="accounting">
            <Route path="chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="journal-entries" element={<JournalEntries />} />
            <Route path="reports" element={<AccountingReports />} />
          </Route>

          {/* Inventory Routes */}
          <Route path="inventory">
            <Route index element={<ItemList />} />
            <Route path="dashboard" element={<StockDashboard />} />
            <Route path="items" element={<ItemList />} />
            <Route path="serial-numbers" element={<SerialNumberDashboard />} />
            <Route path="lot-batch" element={<LotBatchDashboard />} />
            <Route path="cycle-count" element={<CycleCountDashboard />} />
            <Route path="movements" element={<StockMovements />} />
            <Route path="locations" element={<Locations />} />
            <Route path="transfers" element={<TransferOrderList />} />
          </Route>

          {/* Purchase Order Routes */}
          <Route path="purchase-orders">
            <Route index element={<PurchaseOrderList />} />
            <Route path="new" element={<PurchaseOrderForm />} />
            <Route path=":id" element={<PurchaseOrderForm />} />
            <Route path="approvals" element={<PurchaseOrderApproval />} />
            <Route path="receiving" element={<ReceivingInterface />} />
          </Route>

          {/* Sales Order Routes */}
          <Route path="sales-orders">
            <Route index element={<SalesOrderList />} />
            <Route path="new" element={<SalesOrderForm />} />
            <Route path=":id" element={<SalesOrderForm />} />
            <Route path="fulfillment" element={<FulfillmentTracking />} />
            <Route path="shipping" element={<ShippingInterface />} />
          </Route>

          {/* Invoice Routes */}
          <Route path="invoices">
            <Route index element={<InvoiceList />} />
            <Route path="new" element={<InvoiceForm />} />
            <Route path=":id" element={<InvoiceForm />} />
            <Route path=":id/preview" element={<InvoicePreview />} />
          </Route>

          {/* Payment Routes */}
          <Route path="payments">
            <Route index element={<PaymentList />} />
            <Route path="record" element={<Navigate to="/payments?action=create" replace />} />
            <Route path="out" element={<PaymentList />} />
            <Route path="allocate/:id" element={<PaymentAllocation />} />
            <Route path="unallocated" element={<UnallocatedPayments />} />
          </Route>

          {/* BOM Routes */}
          <Route path="bom">
            <Route index element={<BOMList />} />
            <Route path="cost-analysis" element={<BOMList />} />
            <Route path=":id/tree" element={<BOMTreeView />} />
            <Route path=":id/cost-analysis" element={<BOMCostAnalysis />} />
          </Route>

          {/* Assembly Routes */}
          <Route path="assembly">
            <Route index element={<AssemblyOrderList />} />
            <Route path="new" element={<AssemblyWizard />} />
            <Route path=":id/edit" element={<AssemblyWizard />} />
          </Route>

          {/* Demand Planning Routes */}
          <Route path="demand-planning">
            <Route index element={<DemandPlanningDashboard />} />
            <Route path="forecast" element={<ForecastWizard />} />
            <Route path="forecast/:id" element={<ForecastVisualization />} />
            <Route path="forecast/:id/comparison" element={<ForecastComparison />} />
            <Route path="reorder-points" element={<ReorderPointConfig />} />
            <Route path="safety-stock" element={<SafetyStockCalculator />} />
            <Route path="requisitions" element={<PurchaseRequisitionList />} />
            <Route path="requisitions/new" element={<PurchaseRequisitionForm />} />
            <Route path="requisitions/:id" element={<PurchaseRequisitionForm />} />
            <Route path="requisitions/approvals" element={<RequisitionApproval />} />
            <Route path="suggestions" element={<DemandPlanningDashboard />} />
          </Route>

          {/* Reporting Routes */}
          <Route path="reports">
            <Route index element={<ReportSelector />} />
            <Route path="trial-balance" element={<TrialBalance />} />
            <Route path="income-statement" element={<IncomeStatement />} />
            <Route path="balance-sheet" element={<BalanceSheet />} />
            <Route path="cash-flow" element={<CashFlowReport />} />
            <Route path="account-activity" element={<AccountActivity />} />
            <Route path="aging" element={<AgingReport />} />
            <Route path="customer-statements" element={<CustomerStatement />} />
            <Route path="vendor-statements" element={<VendorStatement />} />
            <Route path="inventory" element={<InventoryReports />} />
            <Route path="builder" element={<CustomReportBuilder />} />
            <Route path="scheduled" element={<ScheduledReports />} />
            <Route path="history" element={<ReportHistory />} />
          </Route>

          {/* User Management Routes */}
          <Route path="users">
            <Route index element={<UserList />} />
            <Route path="roles" element={<RoleManagement />} />
          </Route>

          {/* Bills/Expenses Routes */}
          <Route path="expenses">
            <Route index element={<ExpenseList />} />
          </Route>

          {/* Customer Routes */}
          <Route path="customers">
            <Route index element={<CustomerList />} />
          </Route>

          {/* Vendor Routes */}
          <Route path="vendors">
            <Route index element={<VendorList />} />
          </Route>

          {/* Transaction Routes */}
          <Route path="transactions">
            <Route path="cash-ledger" element={<CashLedger />} />
            <Route path="reconciliation-rules" element={<ReconciliationRules />} />
          </Route>

          {/* Banking Routes */}
          <Route path="banking">
            <Route path="accounts" element={<BankAccountList />} />
            <Route path="accounts/:id/transactions" element={<BankAccountList />} />
            <Route path="reconciliation" element={<BankReconciliationIndex />} />
            <Route path="reconciliation/:id" element={<BankReconciliation />} />
            <Route path="statements" element={<BankStatements />} />
            <Route path="transfers" element={<BankTransfers />} />
          </Route>

          {/* Quote Routes */}
          <Route path="quotes">
            <Route index element={<QuoteList />} />
            <Route path=":id/preview" element={<QuotePreview />} />
          </Route>

          {/* RMA Routes */}
          <Route path="rmas">
            <Route index element={<RMAList />} />
          </Route>

          {/* Budget Routes */}
          <Route path="budgets">
            <Route index element={<BudgetList />} />
            <Route path="analysis" element={<BudgetAnalysis />} />
          </Route>

          {/* Fixed Assets Routes */}
          <Route path="assets">
            <Route index element={<AssetList />} />
            <Route path="depreciation" element={<DepreciationSchedule />} />
          </Route>

          {/* Receipt Scanning Routes */}
          <Route path="receipts">
            <Route index element={<ReceiptList />} />
            <Route path="scanner" element={<ReceiptScanner />} />
          </Route>

          {/* Document Management Routes */}
          <Route path="documents">
            <Route index element={<DocumentList />} />
            <Route path=":id" element={<DocumentViewer />} />
            <Route path="templates" element={<TemplateManager />} />
          </Route>

          {/* Project Costing Routes */}
          <Route path="projects">
            <Route index element={<ProjectList />} />
            <Route path="dashboard" element={<ProjectDashboard />} />
            <Route path="time-entries" element={<TimeEntryList />} />
            <Route path=":projectId/profitability" element={<ProjectProfitability />} />
          </Route>

          {/* Organization Routes */}
          <Route path="organizations">
            <Route index element={<OrganizationList />} />
            <Route path="new" element={<OrganizationForm />} />
            <Route path="groups" element={<OrganizationGroups />} />
            <Route path="consolidation" element={<ConsolidatedFinancials />} />
            <Route path="intercompany" element={<IntercompanyTransactions />} />
            <Route path="comparison" element={<OrganizationComparison />} />
            <Route path="cash-flow" element={<GroupCashFlow />} />
            <Route path=":id" element={<OrganizationSettings />} />
            <Route path=":id/edit" element={<OrganizationForm />} />
            <Route path=":id/users" element={<UserManagement />} />
          </Route>

          {/* API Management Routes - Sprint 11 */}
          <Route path="api-management">
            <Route index element={<APIManagementDashboard />} />
            <Route path="keys" element={<APIManagementDashboard />} />
            <Route path="rate-limits" element={<RateLimitingDashboard />} />
            <Route path="webhooks" element={<WebhookManagementDashboard />} />
          </Route>

          {/* Payroll Routes */}
          <Route path="payroll">
            <Route index element={<EmployeeList />} />
            <Route path="employees" element={<EmployeeList />} />
            <Route path="payroll-runs" element={<PayrollRunList />} />
            <Route path="timesheet-entry" element={<TimesheetEntry />} />
            <Route path="timesheet-approval" element={<TimesheetApproval />} />
            <Route path="tax-setup" element={<TaxSetup />} />
            <Route path="pay-stubs" element={<PayStubView />} />
            <Route path="reports" element={<PayrollReports />} />
          </Route>

          {/* Administration Routes - Match Sidebar paths */}
          <Route path="admin">
            <Route path="audit-logs" element={<AuditLogDashboard />} />
            <Route path="multi-region" element={<MultiRegionDashboard />} />
            <Route path="data-retention" element={<DataRetentionSettings />} />
          </Route>

          {/* Audit Routes */}
          <Route path="audit">
            <Route index element={<AuditLogDashboard />} />
            <Route path="logs" element={<AuditLogDashboard />} />
          </Route>

          {/* Performance/Administration Routes - Match Sidebar paths */}
          <Route path="performance">
            <Route path="cache" element={<CacheManagement />} />
            <Route path="archival" element={<ArchivalManagement />} />
            <Route path="analytics" element={<UsageAnalytics />} />
          </Route>

          {/* Navigation Hub */}
          <Route path="navigation" element={<NavigationHub />} />
        </Route>

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
